// ============================================================================
// STORE.REFERRAL_DRAFTS.JS — Routing Draft Persistence (Referral Routing Selector)
//
// Persists the specialty + pathway (pack) selection made in the Routing Selector
// before the final referral submission. This is a separate mutable document from
// the immutable referral doc written at submit time.
//
// Path: /tenants/{tenantId}/referralDrafts/{draftId}
//
// Draft ID convention: `routing_${uid}` — one active routing draft per user.
// This is deterministic, avoids orphan docs, and allows seamless page-reload restore.
//
// Zero-PHI invariants:
//   • No patient name / DOB / MRN / contact in any field.
//   • Only specialty, pathway (pack), and routing metadata.
//   • "selectedPathwayId" = the pack document ID in the codebase ("pathway" in spec).
//
// Invariants:
//   1. saveRoutingDraft uses setDoc (full overwrite) — no partial merge ambiguity.
//   2. getRoutingDraft returns null if the doc does not exist (safe for first use).
//   3. routingStepComplete is only true when both selectedSpecialtyId and
//      selectedPathwayId are present and valid at write time.
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

// ── DB singleton ──────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Save (create or overwrite) a routing draft document.
 *
 * NOTE: "pathway" in the spec/contract maps to "pack" in the codebase.
 *       All external contract fields use "Pathway"; internal code uses "Pack".
 *
 * @param {object} params
 * @param {string} params.tenantId              - Tenant ID (from JWT claim)
 * @param {string} params.draftId               - `routing_${uid}` deterministic key
 * @param {string} params.createdByUid          - UID of the referring provider
 * @param {string} params.selectedSpecialtyId   - Firestore specialty doc ID
 * @param {string} params.selectedSpecialtyName - Human-readable specialty name
 * @param {string} params.selectedPackId        - Pack doc ID (= selectedPathwayId in contract)
 * @param {string} params.selectedPackName      - Pack display name
 * @param {string} [params.selectedPackRef]     - Full Firestore path to pack doc
 * @returns {Promise<void>}
 */
export async function saveRoutingDraft({
  tenantId,
  draftId,
  createdByUid,
  selectedSpecialtyId,
  selectedSpecialtyName,
  selectedPackId,
  selectedPackName,
  selectedPackRef = '',
}) {
  const db  = getDb();
  const ref = doc(db, PATHS.referralDraft(tenantId, draftId));

  await setDoc(ref, {
    id:                    draftId,
    tenantId,
    createdByUid,
    status:                'draft',
    lastCompletedStep:     'routing',
    patientStepComplete:   false,
    routingStepComplete:   true,
    selectedSpecialtyId,
    selectedSpecialtyName,
    // "selectedPathwayId/Name/Ref" matches the external contract spec.
    // These fields hold pack IDs — "pathway" == "pack" in this codebase.
    selectedPathwayId:     selectedPackId,
    selectedPathwayName:   selectedPackName,
    selectedPathwayRef:    selectedPackRef,
    selectionSource:       'manual',
    createdAt:             serverTimestamp(),
    updatedAt:             serverTimestamp(),
  });
}

// ============================================================================
// READ
// ============================================================================

/**
 * Load an existing routing draft.
 *
 * @param {string} tenantId
 * @param {string} draftId - `routing_${uid}`
 * @returns {Promise<object|null>} Draft data or null if not found
 */
export async function getRoutingDraft(tenantId, draftId) {
  const db  = getDb();
  const ref = doc(db, PATHS.referralDraft(tenantId, draftId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}
